import type { IconName } from '@fortawesome/fontawesome-svg-core';
import type { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import type { Theme } from '@mui/material/styles';

import * as SURVEYS from '@constants/survey-ids';

import type { ImageType } from '@models/image-type';

export function resolveIcon(
  surveyId: string,
  theme: Theme
): {
  icon: (Omit<FontAwesomeIconProps, 'color' | 'icon'> & {
    iconName: IconName;
    iconColor?: string;
  })[];
} | null {
  const HEALTH_ICON_COLOR = theme.palette.primary.main;
  const MEDICAL_HISTORY_ICON_COLOR = theme.palette.secondary.main;

  if (!surveyId) {
    return null;
  }
  if (surveyId.includes('vital-sign---')) {
    return {
      icon: [{ iconName: 'monitor-waveform', iconColor: HEALTH_ICON_COLOR }],
    };
  }

  if (surveyId.includes('labs_')) {
    return { icon: [{ iconName: 'vials', iconColor: HEALTH_ICON_COLOR }] };
  }

  switch (surveyId) {
    case SURVEYS.ALLERGY_ID:
      return {
        icon: [{ iconName: 'hand-dots', iconColor: HEALTH_ICON_COLOR }],
      };
    case SURVEYS.APPOINTMENTS_ID:
      return {
        icon: [
          { iconName: 'calendar-days', iconColor: MEDICAL_HISTORY_ICON_COLOR },
        ],
      };
    case SURVEYS.EMERGENCY_VISIT_ID:
      return {
        icon: [
          { iconName: 'truck-medical', iconColor: MEDICAL_HISTORY_ICON_COLOR },
        ],
      };
    case SURVEYS.FAMILY_HISTORY_ID:
      return { icon: [{ iconName: 'users', iconColor: HEALTH_ICON_COLOR }] };
    case SURVEYS.CARRIER_STATUS_ID:
    case SURVEYS.HEALTH_RISK_ID:
      return {
        icon: [{ iconName: 'dna', iconColor: MEDICAL_HISTORY_ICON_COLOR }],
      };
    case SURVEYS.HOSPITALIZATION_ID:
      return {
        icon: [
          { iconName: 'hospital-wide', iconColor: MEDICAL_HISTORY_ICON_COLOR },
        ],
      };
    case SURVEYS.IMAGING_TESTS_ID:
      return {
        icon: [{ iconName: 'images', iconColor: MEDICAL_HISTORY_ICON_COLOR }],
      };
    case SURVEYS.IMMUNIZATION_ID:
      return { icon: [{ iconName: 'syringe', iconColor: HEALTH_ICON_COLOR }] };
    case SURVEYS.MEDICAL_CONDITION_ID:
      return {
        icon: [{ iconName: 'stethoscope', iconColor: HEALTH_ICON_COLOR }],
      };
    case SURVEYS.MEDICATION_ID:
      return { icon: [{ iconName: 'capsules', iconColor: HEALTH_ICON_COLOR }] };
    case SURVEYS.SURGERY_ID:
      return {
        icon: [
          {
            iconName: 'scalpel-line-dashed',
            iconColor: MEDICAL_HISTORY_ICON_COLOR,
          },
        ],
      };
    case SURVEYS.VITAL_SIGN_ID:
      return {
        icon: [{ iconName: 'monitor-waveform', iconColor: HEALTH_ICON_COLOR }],
      };
    case SURVEYS.LAB_RESULTS_ID:
      return { icon: [{ iconName: 'vials', iconColor: HEALTH_ICON_COLOR }] };
    case SURVEYS.INSURANCE_PROGRAMS:
      return {
        icon: [
          {
            iconName: 'address-card',
            iconColor: HEALTH_ICON_COLOR,
          },
        ],
      };
    case SURVEYS.FITNESS_ID:
      return {
        icon: [
          {
            iconName: 'chart-simple',
            iconColor: MEDICAL_HISTORY_ICON_COLOR,
          },
        ],
      };
    case SURVEYS.BODY_ID:
      return {
        icon: [{ iconName: 'person', iconColor: MEDICAL_HISTORY_ICON_COLOR }],
      };
    case SURVEYS.SLEEP_ID:
      return {
        icon: [{ iconName: 'snooze', iconColor: MEDICAL_HISTORY_ICON_COLOR }],
      };
    case SURVEYS.ACTIVITY_ID:
      return {
        icon: [
          {
            iconName: 'person-running-fast',
            iconColor: MEDICAL_HISTORY_ICON_COLOR,
          },
        ],
      };
    case SURVEYS.BODY_SCAN_ID:
      return {
        icon: [
          {
            iconName: 'expand',
            iconColor: MEDICAL_HISTORY_ICON_COLOR,
            size: 'lg',
            transform: { x: -2 },
          },
          {
            iconName: 'person',
            iconColor: MEDICAL_HISTORY_ICON_COLOR,
          },
        ],
      };
    case SURVEYS.FACE_SCAN_ID:
      return {
        icon: [
          {
            iconName: 'face-smile',
            iconColor: HEALTH_ICON_COLOR,
          },
        ],
      };
    case SURVEYS.COUGH_SCAN_ID:
      return {
        icon: [
          {
            iconName: 'head-side-cough',
            iconColor: MEDICAL_HISTORY_ICON_COLOR,
            size: '2xs',
          },
        ],
      };
    case SURVEYS.SKIN_SCAN_ID:
      return {
        icon: [
          {
            iconName: 'person-dots-from-line',
            iconColor: MEDICAL_HISTORY_ICON_COLOR,
            size: 'xs',
          },
        ],
      };
    default:
      return null;
  }
}

export function resolveBackgroundIcon(
  surveyId: ImageType,
  theme: Theme
): { iconName: IconName; iconColor: string } | null {
  const HEALTH_ICON_COLOR = theme.palette.primary.main;
  const MEDICAL_HISTORY_ICON_COLOR = theme.palette.secondary.main;

  switch (surveyId) {
    case 'activity':
      return {
        iconName: 'person-running-fast',
        iconColor: HEALTH_ICON_COLOR,
      };
    case 'allergies':
      return { iconName: 'hand-dots', iconColor: HEALTH_ICON_COLOR };
    case 'appointments':
      return {
        iconName: 'calendar-days',
        iconColor: MEDICAL_HISTORY_ICON_COLOR,
      };
    case 'body':
      return { iconName: 'person', iconColor: HEALTH_ICON_COLOR };
    case 'discern':
      return { iconName: 'brain', iconColor: HEALTH_ICON_COLOR };
    case 'emergencyVisits':
      return {
        iconName: 'truck-medical',
        iconColor: MEDICAL_HISTORY_ICON_COLOR,
      };
    case 'familyHistory':
      return { iconName: 'users', iconColor: HEALTH_ICON_COLOR };
    case 'fitness':
      return { iconName: 'chart-simple', iconColor: HEALTH_ICON_COLOR };
    case 'geneticInformation':
      return { iconName: 'dna', iconColor: MEDICAL_HISTORY_ICON_COLOR };
    case 'hospitalizations':
      return {
        iconName: 'hospital-wide',
        iconColor: MEDICAL_HISTORY_ICON_COLOR,
      };
    case 'imagingTests':
      return { iconName: 'images', iconColor: MEDICAL_HISTORY_ICON_COLOR };
    case 'immunizations':
      return { iconName: 'syringe', iconColor: HEALTH_ICON_COLOR };
    case 'labResults':
      return { iconName: 'vials', iconColor: HEALTH_ICON_COLOR };
    case 'medicalConditions':
      return { iconName: 'stethoscope', iconColor: HEALTH_ICON_COLOR };
    case 'medications':
      return { iconName: 'capsules', iconColor: HEALTH_ICON_COLOR };
    case 'sleep':
      return { iconName: 'snooze', iconColor: HEALTH_ICON_COLOR };
    case 'surgeries':
      return {
        iconName: 'scalpel-line-dashed',
        iconColor: MEDICAL_HISTORY_ICON_COLOR,
      };
    case 'vitalSigns':
      return { iconName: 'monitor-waveform', iconColor: HEALTH_ICON_COLOR };

    case 'logo':
    default:
      return null;
  }
}
